import {FC, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import {
  activateClickFunnelsIntegration,
  deactivateClickFunnelsIntegration,
  fetchActiveIntegrationApp,
  getActivateIntegrationByUser,
  getIntegrationRefetchDataStatus,
  resetSelectedIntegrationData,
} from '../IntegrationSlice/IntegrationSlice'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {Form, Button} from 'react-bootstrap'
import {isLoading} from '../../../../Redux/FrontEnd/Loading/LoadingSlice'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import {ExternalProvider} from '../IntegrationSlice/Types'

interface ClickFunnelsProps {
  selectedExternalProvider: ExternalProvider
  userData: any
}

const ClickFunnels: FC<ClickFunnelsProps> = ({selectedExternalProvider, userData}) => {
  let userId = ''
  if (userData && userData.user) {
    if (userData.user._id !== userData.user.ownerId && !userData.user.subClientAccountId) {
      userId = userData.user.ownerId
    } else {
      userId = userData.user._id
    }
  }

  const selectedActiveIntegration = useSelector(
    getActivateIntegrationByUser(userId, selectedExternalProvider._id)
  )

  const refetch = useSelector(getIntegrationRefetchDataStatus)
  const globalLoading = useSelector(isLoading)

  const dispatch = useDispatch()

  // Handle form
  const activateSchema = Yup.object().shape({
    teamName: Yup.string().required('Team name is required'),
    workspaceName: Yup.string().required('Workspace Name is required'),
    workspaceDomain: Yup.string().required('Workspace Domain is required'),
    apiKey: Yup.string().required('Api Key is required'),
  })

  const formik = useFormik({
    initialValues: {
      teamName: '',
      workspaceName: '',
      workspaceDomain: '',
      apiKey: '',
    },
    validationSchema: activateSchema,
    onSubmit: (values) => {
      dispatch(activateClickFunnelsIntegration(values))
    },
  })

  // load data when first load
  useEffect(() => {
    dispatch(resetSelectedIntegrationData)
    dispatch(fetchActiveIntegrationApp())
  }, [])

  useEffect(() => {
    if (refetch) {
      // console.debug(`[integrationPageDetail] refetch for call recall data`)
      dispatch(fetchActiveIntegrationApp())
    }
  }, [refetch])

  useEffect(() => {
    if (selectedActiveIntegration) {
      formik.resetForm()
    }
  }, [selectedActiveIntegration])

  // handle delete
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState(false)
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false)

  const onclickDeactivateHandler = () => {
    setIsOpenDeleteConfirmation(true)
  }

  const confirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={isOpenDeleteConfirmation}
        handleClose={() => setIsOpenDeleteConfirmation(false)}
        Message={'Are you sure, you want to deactivate integration ?'}
        confirm={() => setIsDeleteConfirm(true)}
        confirmBtnTitle='Yes, deactivate'
      />
    ),
    [isOpenDeleteConfirmation]
  )

  useEffect(() => {
    if (isDeleteConfirm == true) {
      dispatch(deactivateClickFunnelsIntegration())
      setIsDeleteConfirm(false)
      setIsOpenDeleteConfirmation(false)
    }
  }, [isDeleteConfirm])

  return (
    <div className='card p-7 pb-lg-4 p-lg-10'>
      {confirmation}
      <h1>Detail Integration</h1>
      <div className='row'>
        <div className='card-body pt-9 pb-0'>
          <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
            <div className='me-7 mb-4'>
              <div className='symbol symbol-100px symbol-lg-160px symbol-fixed position-relative'>
                {selectedExternalProvider?.image == '' || selectedExternalProvider == undefined ? (
                  <span
                    className='squere1'
                    style={{
                      width: '100px',
                      height: '100px',
                    }}
                  >{`${
                    selectedExternalProvider?.name !== undefined &&
                    selectedExternalProvider?.name !== ''
                      ? selectedExternalProvider?.name.split('')?.[0]?.toUpperCase() ?? 'C'
                      : 'EP'
                  }`}</span>
                ) : (
                  <img src={selectedExternalProvider.image} alt='Cellcast' />
                )}
                <div
                  className={`position-absolute translate-middle bottom-0 start-100 mb-6 rounded-circle border border-4 border-white h-20px w-20px ${
                    selectedActiveIntegration ? ' bg-success ' : 'bg-danger'
                  }`}
                ></div>
              </div>
            </div>
            <div className='flex-grow-1'>
              <span className='text-gray-800 fs-2 fw-bolder me-1 mb-10'>
                {selectedExternalProvider?.name ?? 'Undefined'}
              </span>
              {selectedActiveIntegration ? (
                <div className='mt-5'>
                  <Form.Group className='mb-10'>
                    <Form.Label>Team Id</Form.Label>
                    <Form.Control
                      type='text'
                      value={selectedActiveIntegration?.data.teamId}
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>Team Name</Form.Label>
                    <Form.Control
                      type='text'
                      value={selectedActiveIntegration?.data.teamName}
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>Workspace Id</Form.Label>
                    <Form.Control
                      type='text'
                      value={selectedActiveIntegration?.data.workspaceId}
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>Workspace Name</Form.Label>
                    <Form.Control
                      type='text'
                      value={selectedActiveIntegration?.data.workspaceName}
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>Workspace Domain</Form.Label>
                    <Form.Control
                      type='text'
                      value={selectedActiveIntegration?.data.workspaceDomain}
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>Webhook Id</Form.Label>
                    <Form.Control
                      type='text'
                      value={selectedActiveIntegration?.data.webhookId}
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>Api Key</Form.Label>
                    <Form.Control
                      type='text'
                      disabled
                      value={selectedActiveIntegration?.data.apiKey}
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>Cellcast Api Key Label</Form.Label>
                    <Form.Control
                      type='text'
                      disabled
                      value={selectedActiveIntegration?.internalApiKeyName}
                    />
                    <div>
                      <span className='text-muted'>Visit </span>
                      <Link to='/accounts/api-keys'>
                        <b>api page</b>
                      </Link>
                      <span className='text-muted'> for more detail </span>
                    </div>
                  </Form.Group>
                  <Button
                    variant='danger'
                    type='submit'
                    disabled={globalLoading}
                    onClick={() => onclickDeactivateHandler()}
                  >
                    {globalLoading ? (
                      <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                    ) : (
                      'Deactivate'
                    )}
                  </Button>
                </div>
              ) : (
                <Form onSubmit={formik.handleSubmit} className='form mt-5'>
                  <Form.Group className='mb-10' controlId='teamName'>
                    <Form.Label>Team Name</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter your team`s name'
                      {...formik.getFieldProps('teamName')}
                      isInvalid={formik.touched.teamName && !!formik.errors.teamName}
                    />
                    {formik.touched.teamName && formik.errors.teamName && (
                      <Form.Control.Feedback type='invalid'>
                        {formik.errors.teamName}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Form.Group className='mb-10' controlId='workspaceName'>
                    <Form.Label>Workspace Name</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter your workspace name'
                      {...formik.getFieldProps('workspaceName')}
                      isInvalid={formik.touched.workspaceName && !!formik.errors.workspaceName}
                    />
                    {formik.touched.workspaceName && formik.errors.workspaceName && (
                      <Form.Control.Feedback type='invalid'>
                        {formik.errors.workspaceName}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Form.Group className='mb-10' controlId='workspaceDomain'>
                    <Form.Label>Workspace Domain</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter your workspace domain'
                      {...formik.getFieldProps('workspaceDomain')}
                      isInvalid={formik.touched.workspaceDomain && !!formik.errors.workspaceDomain}
                    />
                    {formik.touched.workspaceDomain && formik.errors.workspaceDomain && (
                      <Form.Control.Feedback type='invalid'>
                        {formik.errors.workspaceDomain}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Form.Group className='mb-10' controlId='apiKey'>
                    <Form.Label>Api Key</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter your api key'
                      {...formik.getFieldProps('apiKey')}
                      isInvalid={formik.touched.apiKey && !!formik.errors.apiKey}
                    />
                    {formik.touched.apiKey && formik.errors.apiKey && (
                      <Form.Control.Feedback type='invalid'>
                        {formik.errors.apiKey}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Button variant='primary' type='submit' disabled={globalLoading}>
                    {globalLoading ? (
                      <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                    ) : (
                      'Activate'
                    )}
                  </Button>
                </Form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {ClickFunnels}
